// Announcement composer: admins reach the whole organization, any forum, or
// hand-picked people, optionally narrowed by role and also sent by email;
// facilitators reach only the forum(s) they facilitate. Recipients see the
// post on Home; push is extra when Firebase is configured.

import { useCallback, useEffect, useState } from 'react';
import { Megaphone, Send, Building2, Users, Clock, UserPlus, X, Mail } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { isAdminRole } from '../utils/roles';
import { broadcastsAPI, forumsAPI, usersAPI } from '../api/client';
import ConfirmModal from '../components/ConfirmModal';
import Button from '../components/Button';
import type { Broadcast as BroadcastRecord, BroadcastAudience } from '../types';

interface ForumOption {
  id: number;
  name: string;
}

interface PersonOption {
  id: number;
  name: string;
  email: string;
}

const ROLE_GROUPS = [
  { id: 'member', label: 'Members', roles: ['member'] },
  { id: 'advisor', label: 'Advisors', roles: ['advisor'] },
  { id: 'guest', label: 'Guests', roles: ['guest'] },
  { id: 'facilitator', label: 'Facilitators', roles: ['facilitator', 'facilitator_admin'] },
  { id: 'admin', label: 'Admins', roles: ['admin', 'facilitator_admin', 'god_mode'] },
];

export default function Broadcast() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);

  const [forums, setForums] = useState<ForumOption[]>([]);
  const [audience, setAudience] = useState<BroadcastAudience>('forum');
  const [forumIds, setForumIds] = useState<number[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [people, setPeople] = useState<PersonOption[]>([]);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleResults, setPeopleResults] = useState<PersonOption[]>([]);
  const [roleGroups, setRoleGroups] = useState<string[]>([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [recent, setRecent] = useState<BroadcastRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadForums = useCallback(async () => {
    const list = isAdmin ? await forumsAPI.getAll() : await forumsAPI.getFacilitating();
    const options = list.map((f) => ({ id: f.id, name: f.name }));
    setForums(options);
    if (!isAdmin) {
      setAudience('forum');
      if (options.length === 1) setForumIds([options[0].id]);
    }
  }, [isAdmin]);

  const loadRecent = useCallback(async () => {
    const rows = await broadcastsAPI.listRecent();
    setRecent(rows);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([loadForums(), loadRecent()])
      .catch(() => setMessage({ type: 'error', text: 'Failed to load broadcast data.' }))
      .finally(() => setIsLoading(false));
  }, [loadForums, loadRecent]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (audience !== 'people' || peopleQuery.trim().length < 2) {
      setPeopleResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const rows = await usersAPI.getAdminMembers({ search: peopleQuery.trim() });
        setPeopleResults(rows.slice(0, 8).map((r) => ({ id: r.id, name: r.full_name || r.username, email: r.email })));
      } catch {
        setPeopleResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [audience, peopleQuery]);

  const selectedForums = forums.filter((f) => forumIds.includes(f.id));
  const roles = Array.from(new Set(ROLE_GROUPS.filter((g) => roleGroups.includes(g.id)).flatMap((g) => g.roles)));
  const canSend =
    title.trim() !== '' &&
    body.trim() !== '' &&
    (audience === 'org' ? isAdmin : audience === 'people' ? people.length > 0 : forumIds.length > 0);

  const roleText =
    roleGroups.length === 0 || audience === 'people'
      ? ''
      : ` (${ROLE_GROUPS.filter((g) => roleGroups.includes(g.id)).map((g) => g.label.toLowerCase()).join(', ')} only)`;
  const audienceDescription =
    (audience === 'org'
      ? 'everyone in the organization'
      : audience === 'people'
        ? people.length === 1
          ? people[0].name
          : `${people.length} selected people`
        : selectedForums.length === 0
          ? 'the selected Forum(s)'
          : selectedForums.length <= 3
            ? `every member of ${selectedForums.map((f) => f.name).join(', ')}`
            : `every member of ${selectedForums.length} Forums`) + roleText;

  const addPerson = (person: PersonOption) => {
    setPeople((prev) => (prev.some((p) => p.id === person.id) ? prev : [...prev, person]));
    setPeopleQuery('');
    setPeopleResults([]);
  };

  const toggleForum = (id: number) => {
    setForumIds((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  };

  const toggleSelectAllForums = () => {
    setForumIds((prev) => (prev.length === forums.length ? [] : forums.map((f) => f.id)));
  };

  const handleSend = async () => {
    if (!canSend || isSending) return;
    setIsSending(true);
    setShowConfirm(false);
    try {
      const sent = await broadcastsAPI.send({
        audience,
        forums: audience === 'forum' ? forumIds : [],
        people: audience === 'people' ? people.map((p) => p.id) : [],
        roles: audience === 'people' ? [] : roles,
        title: title.trim(),
        body: body.trim(),
        send_email: isAdmin && sendEmail,
      });
      setMessage({
        type: 'success',
        text: sent.send_email
          ? `Broadcast sent. Emailing ${sent.email_count} ${sent.email_count === 1 ? 'person' : 'people'} now.`
          : 'Broadcast sent.',
      });
      setTitle('');
      setBody('');
      setSendEmail(false);
      setPeople([]);
      await loadRecent();
    } catch {
      setMessage({ type: 'error', text: 'Failed to send the broadcast.' });
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <Megaphone className="text-navy" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Broadcast</h1>
          <p className="text-sm text-gray-500">
            Send an announcement to {isAdmin ? 'the organization, a Forum, or specific people' : 'your Forum'}.
            Recipients see it on Home. A phone push is extra when their device is set up
            {isAdmin ? ', and you can also send it as an email' : ''}.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-8">
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">Audience</label>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <button
                onClick={() => { setAudience('org'); setForumIds([]); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  audience === 'org'
                    ? 'bg-navy text-white border-navy'
                    : 'border-gray-300 text-gray-600 hover:border-navy'
                }`}
              >
                <Building2 size={14} /> Entire organization
              </button>
            )}
            <button
              onClick={() => setAudience('forum')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                audience === 'forum'
                  ? 'bg-navy text-white border-navy'
                  : 'border-gray-300 text-gray-600 hover:border-navy'
              }`}
            >
              <Users size={14} /> {isAdmin ? 'A specific Forum' : 'My Forum'}
            </button>
            {isAdmin && (
              <button
                onClick={() => { setAudience('people'); setForumIds([]); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  audience === 'people'
                    ? 'bg-navy text-white border-navy'
                    : 'border-gray-300 text-gray-600 hover:border-navy'
                }`}
              >
                <UserPlus size={14} /> Specific people
              </button>
            )}
          </div>
        </div>

        {audience === 'people' && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">People</label>
            {people.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {people.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-navy/10 px-3 py-1 text-sm text-navy">
                    {p.name}
                    <button aria-label={`Remove ${p.name}`} onClick={() => setPeople((prev) => prev.filter((x) => x.id !== p.id))}>
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative">
              <input
                type="text"
                value={peopleQuery}
                onChange={(e) => setPeopleQuery(e.target.value)}
                placeholder="Search by name or email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
              />
              {peopleResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
                  {peopleResults.map((r) => (
                    <button key={r.id} onClick={() => addPerson(r)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                      {r.name} <span className="text-gray-400">{r.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {isAdmin && audience !== 'people' && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Only these roles (optional)</label>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {ROLE_GROUPS.map((g) => (
                <label key={g.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={roleGroups.includes(g.id)}
                    onChange={() => setRoleGroups((prev) => (prev.includes(g.id) ? prev.filter((x) => x !== g.id) : [...prev, g.id]))}
                    className="rounded border-gray-300 text-navy focus:ring-gold"
                  />
                  {g.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {audience === 'forum' && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">
                Forum{forums.length > 1 ? 's' : ''}
              </label>
              {forums.length > 1 && (
                <button
                  type="button"
                  onClick={toggleSelectAllForums}
                  className="text-xs font-medium text-navy hover:underline"
                >
                  {forumIds.length === forums.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>
            {forums.length === 0 ? (
              <p className="text-sm text-gray-400">
                {isAdmin ? 'No Forums found.' : "You don't facilitate any Forums."}
              </p>
            ) : (
              <div className="border border-gray-300 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {forums.map((f) => (
                  <label
                    key={f.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={forumIds.includes(f.id)}
                      onChange={() => toggleForum(f.id)}
                      className="rounded border-gray-300 text-navy focus:ring-gold"
                    />
                    {f.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. Iron Conference announcement"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
          />
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            placeholder="What should recipients know?"
            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
          />
        </div>

        {isAdmin && (
          <label className="mb-4 flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-navy focus:ring-gold"
            />
            <span>
              <span className="font-medium">Also send as email.</span>{' '}
              <span className="text-gray-500">
                You get a copy too. The email layout is the “Broadcast email” template under Admin → Emails.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500 max-w-sm">
            This appears on Home for {audienceDescription}. Push is sent when their phone is set up
            {sendEmail && isAdmin ? ', and everyone gets an email' : ''}.
          </p>
          <Button
            variant="primary"
            icon={Send}
            disabled={!canSend || isSending}
            loading={isSending}
            onClick={() => setShowConfirm(true)}
          >
            Send
          </Button>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Recent broadcasts
        </h2>
        {recent.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-xl shadow-sm border border-gray-200">
            <Megaphone className="mx-auto text-gray-300" size={36} />
            <p className="text-gray-500 mt-2 text-sm">No broadcasts sent yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recent.map((b) => (
              <div key={b.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          b.audience === 'org' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }`}
                      >
                        {b.audience === 'org'
                          ? 'Entire organization'
                          : b.audience === 'people'
                            ? b.people_names.length <= 2
                              ? b.people_names.join(', ')
                              : `${b.people_names.length} people`
                            : b.forum_names.length === 0
                            ? 'Forum'
                            : b.forum_names.length <= 2
                              ? b.forum_names.join(', ')
                              : `${b.forum_names.length} Forums`}
                      </span>
                      {b.send_email && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                          <Mail size={11} /> Emailed {b.email_count}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-gray-900">{b.title}</p>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{b.body}</p>
                  </div>
                  <div className="text-right shrink-0 text-xs text-gray-400">
                    <p className="flex items-center gap-1 justify-end">
                      <Clock size={12} /> {new Date(b.sent_at).toLocaleDateString()}
                    </p>
                    <p className="mt-1">{b.recipient_count} recipients</p>
                    <p className="mt-0.5">by {b.sender_name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showConfirm}
        title="Send this broadcast?"
        message={`This will post to Home for ${audienceDescription}, and push to phones that are set up${
          sendEmail && isAdmin ? '. Everyone also gets an email' : ''
        }. This can't be undone.`}
        confirmLabel="Send it"
        variant="primary"
        onConfirm={handleSend}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
